import {
  type SessionOutcomeKind,
  SESSION_OUTCOME_KINDS,
  VERDICT_CONFIDENCES,
} from '@sbc/domain-models/agents';
import { Link, useNavigate } from '@tanstack/react-router';
import { X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useSessionOutcomes } from '@/lib/api/outcomes';

import {
  isOutcomesTabActive,
  type OutcomeConfidence,
  OUTCOME_TABS,
  type OutcomesListSearch,
} from './outcomes-search';

const PAGE_SIZE = 20;
const ALL = 'all';

// A display map over a closed enum, not a business branch: every row still renders from
// its projection.
const KIND_VARIANT: Record<SessionOutcomeKind, 'secondary' | 'default' | 'outline'> = {
  'root-cause-verdict': 'default',
  'reply-draft': 'secondary',
  'cs-draft': 'secondary',
  'channel-reply': 'outline',
  'job-result': 'outline',
  'product-registration-document-check': 'outline',
  'kb-edit-proposal': 'default',
  'prompt-rule-proposal': 'default',
  'draft-review': 'outline',
  'cs-decision': 'secondary',
  'draft-claims': 'outline',
};

const CONFIDENCE_VARIANT: Record<OutcomeConfidence, 'secondary' | 'outline' | 'destructive'> = {
  high: 'secondary',
  medium: 'outline',
  low: 'destructive',
};

const LINEAR_ISSUE_URL = 'https://linear.app/seoul-beauty-club/issue/';

export function OutcomesListPage({ search }: { search: OutcomesListSearch }) {
  const navigate = useNavigate();
  const page = search.page ?? 1;

  const update = (patch: Partial<OutcomesListSearch>) => {
    void navigate({
      to: '/outcomes',
      search: {
        needsReview: search.needsReview,
        kind: search.kind,
        confidence: search.confidence,
        groupKey: search.groupKey,
        page: undefined,
        ...patch,
      },
    });
  };

  const outcomesQuery = useSessionOutcomes({
    page,
    pageSize: PAGE_SIZE,
    needsReview: search.needsReview,
    kind: search.kind,
    confidence: search.confidence,
    groupKey: search.groupKey,
  });

  const result = outcomesQuery.data;
  const pageCount = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Outcomes</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          What every agent concluded — verdicts, drafts and replies — in one list.
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-md border p-1">
            {OUTCOME_TABS.map((tab) => (
              <Button
                key={tab.label}
                size="sm"
                variant={isOutcomesTabActive(tab, search) ? 'secondary' : 'ghost'}
                onClick={() => update(tab.filter)}
              >
                {tab.label}
              </Button>
            ))}
          </div>
          <Select
            value={search.kind ?? ALL}
            onValueChange={(value) =>
              update({
                kind: SESSION_OUTCOME_KINDS.find((candidate) => candidate === value),
              })
            }
          >
            <SelectTrigger aria-label="Filter by kind" className="w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All kinds</SelectItem>
              {SESSION_OUTCOME_KINDS.map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={search.confidence ?? ALL}
            onValueChange={(value) =>
              update({
                confidence: VERDICT_CONFIDENCES.find((candidate) => candidate === value),
              })
            }
          >
            <SelectTrigger aria-label="Filter by confidence" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All confidences</SelectItem>
              {VERDICT_CONFIDENCES.map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {search.groupKey && (
            <Button variant="outline" size="sm" onClick={() => update({ groupKey: undefined })}>
              {search.groupKey}
              <X className="size-3.5" />
            </Button>
          )}
        </div>
        {search.needsReview && (
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Held by the kind&apos;s own rule: a bug confirmed with low confidence, or a draft that
            is low-confidence, carries an injection risk, or needs a human to act first.
          </p>
        )}
      </div>

      {outcomesQuery.isPending && <p className="text-sm">Loading…</p>}
      {outcomesQuery.isError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Failed to load outcomes: {outcomesQuery.error.message}
        </p>
      )}

      {result && (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kind</TableHead>
                  <TableHead>Session</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Confidence</TableHead>
                  <TableHead className="w-1/3">Summary</TableHead>
                  <TableHead>Group</TableHead>
                  <TableHead>Ref</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.items.length ? (
                  result.items.map((outcome) => (
                    <TableRow key={outcome.id}>
                      <TableCell>
                        <Badge variant={KIND_VARIANT[outcome.kind]}>{outcome.kind}</Badge>
                      </TableCell>
                      <TableCell>
                        <Link
                          to="/sessions/$sessionId"
                          params={{ sessionId: outcome.sessionId }}
                          className="font-medium hover:underline"
                        >
                          {outcome.sessionName ?? 'Untitled session'}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm">{outcome.agentName}</TableCell>
                      <TableCell className="text-sm">{outcome.projection?.label ?? '—'}</TableCell>
                      <TableCell>
                        {outcome.projection?.confidence ? (
                          <Badge variant={CONFIDENCE_VARIANT[outcome.projection.confidence]}>
                            {outcome.projection.confidence}
                          </Badge>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell
                        className="max-w-md truncate text-sm text-[var(--color-muted-foreground)]"
                        title={outcome.projection?.summary}
                      >
                        {outcome.projection?.summary ?? (
                          <span className="italic">Payload no longer readable</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {outcome.groupKey ? (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => update({ groupKey: outcome.groupKey ?? undefined })}
                              className="hover:underline"
                            >
                              {outcome.groupKey}
                            </button>
                            {outcome.repeatCount !== null && outcome.repeatCount > 1 && (
                              <Badge variant="outline">×{outcome.repeatCount}</Badge>
                            )}
                          </div>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {outcome.projection?.externalRef ? (
                          <a
                            href={`${LINEAR_ISSUE_URL}${outcome.projection.externalRef}`}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:underline"
                          >
                            {outcome.projection.externalRef}
                          </a>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(outcome.createdAt).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} className="h-24 text-center">
                      Nothing here.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              {result.total} outcome(s)
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => update({ page: page - 1 > 1 ? page - 1 : undefined })}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <span className="text-sm text-[var(--color-muted-foreground)]">
                Page {page} of {pageCount}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => update({ page: page + 1 })}
                disabled={page >= pageCount}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
